/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import adminConfigService, {
  toSelectOptions,
  UploadConfig,
} from "services/AdminConfig/adminConfigService";
import { useUploadConfig } from "hooks/useUploadConfig";
import { inventoryOptions } from "./inventoryValues";

export interface SelectOption {
  value: string;
  label: string;
}

// Admin-configured asset types may be stored in any case (e.g. "RENTAL", "Own").
// Normalize before comparing against the known business categories so the
// asset-type conditional fields / payload mapping work regardless of casing.
export const normalizeAssetType = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();

// ── Fallbacks ────────────────────────────────────────────────────────────────
// Used while the Admin Config calls are in flight or if they fail, so the forms
// stay usable. They mirror the values the module shipped with historically.
const FALLBACK_ASSET_TYPES: SelectOption[] = [
  { value: "Own", label: "Own" },
  { value: "Rental", label: "Rental" },
  { value: "Sample", label: "Sample" },
];
const FALLBACK_DEVICE_STATUSES: SelectOption[] = [
  { value: "Working", label: "Working" },
  { value: "Not Working", label: "Not Working" },
];
// Device types historically came from inventoryValues.inventoryOptions.
const FALLBACK_DEVICE_TYPES: SelectOption[] = inventoryOptions;

const FALLBACK_UPLOAD_CONFIG: UploadConfig = {
  module: "ASSET",
  maxFiles: 3,
  maxFileSizeKb: 2048, // 2 MB — matches the previous hardcoded limit
  maxFileSizeMb: 2,
  allowedFileTypes: ["JPEG", "JPG", "PNG"],
};

/**
 * Admin-configured option lists for the inventory module (Asset Type, Device
 * Type, Device Status). Each is rendered dynamically from Admin Config with a
 * static fallback. `loaded` flips true once all three requests settle.
 */
export const useInventoryOptions = () => {
  const [assetTypeOptions, setAssetTypeOptions] = useState<SelectOption[]>(FALLBACK_ASSET_TYPES);
  const [deviceTypeOptions, setDeviceTypeOptions] = useState<SelectOption[]>(FALLBACK_DEVICE_TYPES);
  const [deviceStatusOptions, setDeviceStatusOptions] =
    useState<SelectOption[]>(FALLBACK_DEVICE_STATUSES);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      adminConfigService.getAssetTypes(),
      adminConfigService.getDeviceTypes(),
      adminConfigService.getDeviceStatuses(),
    ]).then(([assetRes, deviceRes, statusRes]) => {
      if (!active) return;
      if (assetRes.status === "fulfilled" && assetRes.value.data?.length) {
        setAssetTypeOptions(toSelectOptions(assetRes.value.data));
      }
      if (deviceRes.status === "fulfilled" && deviceRes.value.data?.length) {
        setDeviceTypeOptions(toSelectOptions(deviceRes.value.data));
      }
      if (statusRes.status === "fulfilled" && statusRes.value.data?.length) {
        setDeviceStatusOptions(toSelectOptions(statusRes.value.data));
      }
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, []);

  return { assetTypeOptions, deviceTypeOptions, deviceStatusOptions, loaded };
};

/**
 * Inventory upload policy (allowed file types + max count + max size) sourced
 * from `api/upload-config/ASSET`. Thin wrapper over the shared `useUploadConfig`
 * so the validation logic lives in one place (util/uploadConfig). Falls back to
 * the previous hardcoded 2 MB / 3-file / image policy while loading or on failure.
 */
export const useInventoryUploadConfig = () =>
  useUploadConfig("ASSET", FALLBACK_UPLOAD_CONFIG);
